import { BUILDER_DATA_MODELS, BUILDER_PAGE_MODELS, BannerType } from '@/hoc/withBuilderPageData';
import { ArticlePreview, ArticlePreviewWithPagination, BuilderArticlePage, Category, Tag } from '@/types/article';
import { Author, AuthorContent } from '@/types/author';
import { Banner } from '@/types/banner';
import { CampaignResponseBody, CampaignState } from '@/types/campaign';
import { BuilderHelpCentrePage, HelpCentreFaq, HelpCentreFaqEnriched, HelpCentrePreview } from '@/types/helpCentre';
import { NavigationItem } from '@/types/navigation';
import { BuilderContent, builder } from '@builder.io/sdk';
import { fetchWithToken } from './fetch';
import { frontCoverPageSize } from '@/config';
import { FooterData } from '@/types/footer';
import { isPreview, SearchParams } from '@/utils/isPreview';
import { cache } from 'react';

export interface NavigationResponse extends BuilderContent {
  data: NavigationItem;
}

export interface FooterResponse extends BuilderContent {
  data: FooterData;
}

export interface AuthorResponse extends BuilderContent {
  data: Author;
}

export const getAllCategories = cache(async () => {
  const results = await builder.getAll(BUILDER_PAGE_MODELS.category, {
    fields: 'data,id,name',
    limit: 100,
    options: {
      noTargeting: true,
      includeUnpublished: isPreview(),
    },
  });
  return results.filter((c) => c.id) as BuilderContent[];
});

export const getAllExperts = async () => {
  // With a boolean value in Builder, if it is not set to true at least once, it will be undefined.
  const authorResponse = (await builder.getAll('author-page', {
    model: 'author-page',
    fields: 'data.name,data.url,data.avatar,data.jobtitle,data.story,data.linkedin,data.email',
    query: {
      'data.isExpert': {
        $eq: true,
      },
    },
    options: {
      noTargeting: true,
      includeRefs: true,
      includeUnpublished: isPreview(),
    },
  })) as AuthorContent[];

  return authorResponse.map((author) => author.data);
};

/*
 * Builder.io API does not support getting the total count of entries for a model directly.
 *
 * builder.getContent() tops out at 100 entries per request.
 * This function fetches all entries in batches of 100 until no more entries are returned.
 * It returns the total count of entries for the specified model.
 *
 * Beware - for some reason builder.getAll() returns only 30 entries at a time, so we cannot use it here.
 */
export const getTotalEntryCountForModel = async ({
  model,
  query,
}: {
  model: string;
  query?: Record<string, unknown>;
}): Promise<number> => {
  const pageSize = 100;

  let totalFetched = 0;
  let currentOffset = 0;

  while (true) {
    const batch = await builder.getContent(model, {
      model,
      query,
      options: {
        noTargeting: true,
        includeRefs: false,
        fields: '_id',
        limit: pageSize,
        offset: currentOffset,
        includeUnpublished: isPreview(),
      },
    });

    totalFetched += batch.length;

    if (batch.length < pageSize) break;
    currentOffset += pageSize;
  }

  return totalFetched;
};

export const getArticlePreviews = async (params?: {
  excludeFeatured?: boolean;
  onlyFeatured?: boolean;
  tagId?: string;
  categoryId?: string;
  categoryIds?: string[];
  page?: number;
  limit?: number;
  authorId?: string;
  pageModel?: string;
}) => {
  const pageModel = params?.pageModel ?? BUILDER_PAGE_MODELS.article;
  const query: Record<string, unknown> = {};

  if (params?.onlyFeatured) {
    query['data.featured'] = true;
  } else if (params?.excludeFeatured) {
    query.$or = [{ 'data.featured': { $eq: false } }, { 'data.featured': { $exists: false } }];
  }

  if (params?.tagId) {
    query['data.tags.tag.id'] = params.tagId;
  }

  if (params?.categoryId) {
    query['data.category.id'] = params.categoryId;
  }

  if (params?.categoryIds && params.categoryIds.length > 0) {
    query['data.category.id'] = { $in: params.categoryIds };
  }

  if (params?.authorId) {
    query['data.authorPage.id'] = params.authorId;
  }

  const page = params?.page ?? 1;
  const limit = params?.limit ?? frontCoverPageSize;
  const offset = (page - 1) * limit;

  const [allArticles, paginatedArticles] = await Promise.all([
    getTotalEntryCountForModel({
      model: pageModel,
      query,
    }),
    builder.getAll(pageModel, {
      model: pageModel,
      fields: 'data.title,data.url,data.heroImage,data.category,data.tags,data.featured,lastUpdated',
      query,
      limit,
      offset,
      options: {
        noTargeting: true,
        includeRefs: true,
        includeUnpublished: isPreview(),
      },
    }),
  ]);

  const totalPages = Math.ceil(allArticles / limit);

  return {
    articles: paginatedArticles.map((article) => ({
      ...article.data,
      lastUpdated: article.lastUpdated,
    })) as ArticlePreview[],
    currentPage: page,
    totalPages,
    limit,
  } as ArticlePreviewWithPagination;
};

export const getAllArticlePreviews = async (params?: {
  excludeFeatured?: boolean;
  onlyFeatured?: boolean;
  tagId?: string;
  categoryId?: string;
  categoryIds?: string[];
  authorId?: string;
  pageModel?: string;
}) => {
  const pageModel = params?.pageModel ?? BUILDER_PAGE_MODELS.article;
  const pageSize = 100;
  const query: Record<string, unknown> = {};

  if (params?.onlyFeatured) {
    query['data.featured'] = true;
  } else if (params?.excludeFeatured) {
    query.$or = [{ 'data.featured': { $eq: false } }, { 'data.featured': { $exists: false } }];
  }

  if (params?.tagId) {
    query['data.tags.tag.id'] = params.tagId;
  }

  if (params?.categoryId) {
    query['data.category.id'] = params.categoryId;
  }

  if (params?.categoryIds && params.categoryIds.length > 0) {
    query['data.category.id'] = { $in: params.categoryIds };
  }

  if (params?.authorId) {
    query['data.authorPage.id'] = params.authorId;
  }

  const allBatches: BuilderContent[] = [];
  let currentOffset = 0;

  while (true) {
    const batch = await builder.getContent(pageModel, {
      model: pageModel,
      query,
      options: {
        noTargeting: true,
        includeRefs: true,
        fields: 'data.title,data.url,data.heroImage,data.category,data.tags,data.featured,lastUpdated',
        limit: pageSize,
        offset: currentOffset,
        includeUnpublished: isPreview(),
      },
    });

    allBatches.push(...batch);
    if (batch.length < pageSize) break;
    currentOffset += pageSize;
  }

  return allBatches.map((article) => ({
    ...article.data,
    lastUpdated: article.lastUpdated,
  })) as ArticlePreview[];
};

export const getLatestArticlePreviews = cache(
  async (limit: number = 6, pageModel: string = BUILDER_PAGE_MODELS.article) => {
    const articles = await builder.getAll(pageModel, {
      fields: 'data.title,data.url,data.heroImage,data.category,data.tags,data.featured,lastUpdated,firstPublished',
      limit,
      sort: {
        firstPublished: -1, // sort by publication date, newest first
      },
      options: {
        noTargeting: true,
        includeRefs: true,
        includeUnpublished: isPreview(),
      },
    });

    const mappedArticles = articles.map((article) => ({
      ...article.data,
      lastUpdated: article.lastUpdated,
      firstPublished: article.firstPublished,
    })) as ArticlePreview[];

    return {
      articles: mappedArticles,
      currentPage: 1,
      totalPages: 1,
      limit,
    } as ArticlePreviewWithPagination;
  }
);

export const getArticlePreviewsByTagId = async (tagId?: string, pageModel: string = BUILDER_PAGE_MODELS.article) => {
  return getArticlePreviews({
    tagId,
    pageModel,
  });
};

export const getAllHelpCentrePreviews = async () => {
  const helpCentrePages = await builder.getAll('help-centre', {
    fields: 'data.title,data.description,data.url,data.featuredImage',
    options: { noTargeting: true, includeUnpublished: isPreview() },
  });

  return helpCentrePages as HelpCentrePreview[];
};

export const getHelpCentrePage = cache(
  async (urlPath: string, includeUnpublished?: boolean): Promise<BuilderContent | undefined> => {
    return await builder
      .get(BUILDER_PAGE_MODELS.helpCentre, {
        userAttributes: {
          urlPath: `/help-centre/${urlPath}`,
        },
        includeUnpublished: includeUnpublished ?? isPreview(),
      })
      .toPromise();
  }
);

export const getHelpCentreFaqs = async () => {
  const helpPages = (await builder.getAll('help-centre', {
    fields: 'data.faqs,data.title,data.url',
    options: { noTargeting: true, includeUnpublished: isPreview() },
  })) as BuilderHelpCentrePage[];

  const enrichedHelpFaqArray = helpPages.map((page: BuilderHelpCentrePage) =>
    page.data?.faqs.map(
      (faq: HelpCentreFaq) =>
        ({ ...faq, parentTitle: page.data?.title, parentUrl: page.data?.url }) as HelpCentreFaqEnriched
    )
  );

  const allFaqs = enrichedHelpFaqArray.flat();
  return allFaqs;
};

export const getAuthor = cache(
  async (urlPath: string, includeUnpublished?: boolean): Promise<AuthorContent | undefined> => {
    return await builder
      .get(BUILDER_PAGE_MODELS.author, {
        userAttributes: {
          urlPath: `/our-experts/${urlPath}`,
        },
        includeUnpublished: includeUnpublished ?? isPreview(),
      })
      .toPromise();
  }
);

export const getPage = cache(
  async (urlPath: string, includeUnpublished?: boolean): Promise<BuilderContent | undefined> => {
    return await builder
      .get(BUILDER_PAGE_MODELS.page, {
        userAttributes: {
          urlPath: `/${urlPath}`,
        },
        includeRefs: true,
        includeUnpublished: includeUnpublished ?? isPreview(),
      })
      .toPromise();
  }
);

export const getAllPageModel = async (
  model: string,
  fields: string = 'data',
  isIndexable: boolean = false
): Promise<BuilderContent[]> => {
  const totalPages = await getTotalEntryCountForModel({ model: model });
  const batchCount = Math.ceil(totalPages / 100);
  const batchedRequests = [];
  for (let i = 0, l = batchCount; i < l; i++) {
    batchedRequests.push(
      await builder.getAll(model, {
        limit: 100,
        offset: i * 100,
        fields: fields,
        query: isIndexable ? { 'data.indexable': true } : null,
        options: { noTargeting: true, includeUnpublished: isPreview() },
      })
    );
  }
  return batchedRequests.flat();
};

export type ArticleSection = 'front-cover' | 'guides' | 'news';

export const getArticleBySection = cache(
  async (
    section: ArticleSection,
    slug?: string,
    includeUnpublished?: boolean
  ): Promise<BuilderArticlePage | undefined> => {
    return await builder
      .get(BUILDER_PAGE_MODELS.article, {
        userAttributes: {
          urlPath: `/${section}/${slug}`,
        },
        includeRefs: true,
        includeUnpublished: includeUnpublished ?? isPreview(),
      })
      .toPromise();
  }
);

export const getArticle = (slug?: string, searchParams?: SearchParams) =>
  getArticleBySection('front-cover', slug, isPreview(searchParams));
export const getGuideArticle = (slug?: string, searchParams?: SearchParams) =>
  getArticleBySection('guides', slug, isPreview(searchParams));
export const getNewsArticle = (slug?: string, searchParams?: SearchParams) =>
  getArticleBySection('news', slug, isPreview(searchParams));

export const getNewsRoomArticle = cache(
  async (slug?: string, includeUnpublished?: boolean): Promise<BuilderArticlePage | undefined> => {
    return await builder
      .get(BUILDER_PAGE_MODELS.newsRoom, {
        userAttributes: {
          urlPath: `/news-room/${slug}`,
        },
        includeRefs: true,
        includeUnpublished: includeUnpublished ?? isPreview(),
      })
      .toPromise();
  }
);

export const getTag = cache(async (slug?: string, includeUnpublished?: boolean): Promise<Tag | undefined> => {
  return await builder
    .get(BUILDER_PAGE_MODELS.tag, {
      userAttributes: {
        urlPath: `/tag/${slug}`,
      },
      includeUnpublished: includeUnpublished ?? isPreview(),
    })
    .toPromise();
});

export const getNavigation = cache(async (): Promise<NavigationItem[]> => {
  const navigation = (await builder.getAll('navigation', {
    model: 'navigation',
    options: {
      noTargeting: true,
      includeRefs: true,
      includeUnpublished: isPreview(),
    },
  })) as NavigationResponse[];

  if (!navigation || navigation.length === 0) {
    return [];
  }

  return navigation.map((nav) => ({
    ...nav.data,
  }));
});

export const getFooter = cache(async (): Promise<FooterData> => {
  const footer = (await builder.get('footer', {
    model: 'footer',
    fields: 'data',
    options: {
      noTargeting: true,
      includeUnpublished: isPreview(),
    },
  })) as FooterResponse;
  return footer.data as FooterData;
});

export const getBanner = cache(async (name: BannerType): Promise<Banner | undefined> => {
  return (await builder
    .get(BUILDER_DATA_MODELS.banner, {
      query: {
        'data.name': {
          $eq: name,
        },
      },
      includeUnpublished: isPreview(),
    })
    .toPromise()) as Banner;
});

export const getCategoryBySection = cache(
  async (section: ArticleSection, slug?: string, includeUnpublished?: boolean): Promise<Category | undefined> => {
    return await builder
      .get(BUILDER_PAGE_MODELS.category, {
        userAttributes: {
          urlPath: `/${section}/${slug}`,
        },
        includeUnpublished: includeUnpublished ?? isPreview(),
      })
      .toPromise();
  }
);

export const getCategory = (slug?: string, searchParams?: SearchParams) =>
  getCategoryBySection('front-cover', slug, isPreview(searchParams));
export const getGuideCategory = (slug?: string, searchParams?: SearchParams) =>
  getCategoryBySection('guides', slug, isPreview(searchParams));
export const getNewsCategory = (slug?: string, searchParams?: SearchParams) =>
  getCategoryBySection('news', slug, isPreview(searchParams));

export const getCategoryIdsBySection = cache(async (section: ArticleSection): Promise<string[]> => {
  const categories = await builder.getAll(BUILDER_PAGE_MODELS.category, {
    model: BUILDER_PAGE_MODELS.category,
    fields: 'id,data.url',
    options: {
      noTargeting: true,
      includeUnpublished: isPreview(),
    },
  });

  return categories
    .filter((c) => (c.data as { url?: string })?.url?.startsWith(`/${section}`))
    .map((c) => c.id)
    .filter(Boolean) as string[];
});

export const verifyCampaign = async (utmCampaignRef: string): Promise<CampaignState | null> => {
  try {
    const { CampaignActive, CampaignExists, CampaignRef } = await fetchWithToken<CampaignResponseBody>(
      `https://${process.env.API_ENDPOINT}/api/campaign/${utmCampaignRef}/verify`
    );

    return {
      campaignRef: CampaignRef,
      campaignActive: CampaignActive,
      campaignExists: CampaignExists,
    };
  } catch (error) {
    console.error('Error verifying campaign:', error);
    return null;
  }
};
